import { animate, MotionValue, useDragControls } from 'framer-motion';
import { useCallback, useState } from 'react';

export type BottomSheetDragOptions = {
  onClose: (velocity: number) => void;
  closeDistance?: number; // px
  closeVelocity?: number; // px/s
  snapSpring?: { stiffness?: number; damping?: number; mass?: number };
  y: MotionValue<number>;
  dim: MotionValue<number>;
};

export function useBottomSheetDrag({ onClose, y, dim, closeDistance = 56, closeVelocity = 600, snapSpring = { stiffness: 420, damping: 32, mass: 0.8 } }: BottomSheetDragOptions) {
  const controls = useDragControls();
  const [dragging, setDragging] = useState(false);

  const onDragStart = useCallback(() => {
    setDragging(true);
  }, []);

  const setOffset = useCallback(
    (offset: number) => {
      const clamped = Math.max(0, offset);
      y.set(clamped);
      dim.set(1 - clamped / window.innerHeight);
    },
    [y, dim]
  );

  const onDrag = useCallback((_: any, info: { offset: { y: number } }) => setOffset(info.offset.y), [setOffset]);

  const release = useCallback(
    (offset: number, velocity: number) => {
      if (offset > closeDistance || velocity > closeVelocity) {
        onClose(Math.max(0, velocity));
      } else {
        animate(y, 0, { type: 'spring', ...snapSpring });
        animate(dim, 1, { type: 'spring', ...snapSpring });
      }
    },
    [closeDistance, closeVelocity, onClose, snapSpring, y, dim]
  );

  const onDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: { offset: { y: number }; velocity: { y: number } }) => {
      setDragging(false);
      release(info.offset.y, info.velocity.y);
    },
    [release]
  );

  return {
    controls,
    y,
    dragging,
    onDragStart,
    onDrag,
    onDragEnd,
    release,
    setOffset,
  };
}
